import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle"
import { Autocomplete } from "@material-ui/lab"
import dayjs from "dayjs"
import { Form, Formik } from "formik"
import React, { useContext, useRef, useState } from "react"
import { CopyToClipboard } from "react-copy-to-clipboard"
import ReactPlayer from "react-player"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"
import * as Yup from "yup"
import { listPublishedCourses, setAccessCode } from "~/actions/clients"
import MaskedSeats from "~/components/Clients/MaskedSeats"
import AccessCodeVideoUpload from "~/components/Common/AccessCodeVideoUpload"
import Box from "~/components/Common/Box"
import CounterHeader from "~/components/Common/CounterHeader"
import HoverText from "~/components/Common/HoverText"
import LogoUpload from "~/components/Common/LogoUpload"
import App from "~/contexts/App"
import ClientsContext from "~/contexts/Clients"
import serialize from "~/store/serialize"
import useStyles from "./styles"

const schema = Yup.object().shape({
  code: Yup.string()
    .test(
      "len",
      "Access code hast to be at least 5 characters length",
      (val) => val?.length >= 5,
    )
    .required(`Access Code is required`),
  is_active: Yup.string(),
  seats: Yup.number().moreThan(0).required("Total Seats Availabe is required"),
  course_containers: Yup.array().test({
    message: "Select at least one course",
    test: (arr) => arr.length > 0,
  }),
  description: Yup.string()
    .required("Welcome Description is required")
    .max(400, "Description must be less than 400 characters"),
  image: Yup.string().required("A logo is required"),
  amazon_sso: Yup.bool(),
  facebook_sso: Yup.bool(),
  github_sso: Yup.bool(),
  google_sso: Yup.bool(),
  video: Yup.string().nullable(true),
  expiration: Yup.date().required("Expiration Date is required"),
})

const CreateEditClientDialog = () => {
  const formRef = useRef()
  const classes = useStyles()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { dialogs, closeDialog, openDialog, openSnackBar } = useContext(App)
  const thisDialog = dialogs?.["createEditAccessLink"] || {}
  const { open = false } = thisDialog
  const client = thisDialog?.data?.client
  const accessLink = thisDialog?.data?.accessLink || {}
  const action = thisDialog?.data?.action || {}
  const history = useHistory()
  //save loading state
  const [loading, setLoading] = useState(false)
  const { checkAccessCodeMutation } = useContext(ClientsContext)
  //video loading to disable save button
  const [videoLoading, setVideoLoading] = useState(false)
  const defaultExpYear = dayjs().year() + 1
  const defaultExpiration = dayjs().year(defaultExpYear).hour(23).minute(59)
  const [clearTextField, setClearTextField] = useState(false)
  const [uploadImg, setUploadImg] = useState(null)
  const [uploadVideo, setUploadVideo] = useState(null)
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))
  const [accessCodeUsed, setAccessCodeUsed] = useState(false)
  const setAccessCodeMutation = useMutation((data) =>
    setAccessCode(data, client?.id),
  )
  const { data: publishedCourses } = useQuery("publishedCourses", () =>
    listPublishedCourses(),
  )

  const handleClose = () => {
    closeDialog("createEditAccessLink", false)
    setUploadVideo(null)
    setUploadImg(null)
    setAccessCodeUsed(false)
  }

  const checkAccessCode = (code) => {
    return new Promise((resolve, reject) => {
      let data = { code: code }
      checkAccessCodeMutation
        .mutateAsync(data)
        .then(() => {
          setLoading(false)
          setAccessCodeUsed(true)
          openSnackBar({ message: "Access Code in use" })

          reject()
        })
        .catch((err) => {
          resolve()
          setAccessCodeUsed(true)
        })
    })
  }

  const createAccessCode = (data) => {
    return new Promise((resolve, reject) => {
      setAccessCodeMutation
        .mutateAsync(data)
        .then((res) => {
          serialize("client", res.data).then((serializedData) => {
            return queryClient.setQueryData("clients", (oldData) => {
              let newArray = []
              newArray = oldData.map((x) => {
                if (x.id !== serializedData.id) {
                  return x
                } else {
                  return serializedData
                }
              })
              return newArray
            })
          })
          resolve()
        })
        .catch((err) => {
          reject()
          openSnackBar({ message: "Error. Failed to update Client" })
          setLoading(false)
        })
    })
  }

  //function to modify the data
  const newCodesFunc = (values) => {
    let newCodes = []
    if (action === "edit") {
      newCodes = client.access_codes.map((a) => {
        //create  course containers array for the access code that is being crating
        if (Number(a?.id) === Number(values?.id)) {
          let data = values
          data.course_containers = data.course_containers.map(
            (x) => x.container_id,
          )
          data.is_active = values?.is_active === "active" ? 1 : 0
          return data
        } else {
          //create  course containers array for the otheres access codes
          let data = a
          data.course_containers = a.courses.map((x) => x.container_id)
          return data
        }
      })
    } else {
      //create  course containers array for the otheres access codes
      let newClientsAccess = client.access_codes.map((a) => {
        a.course_containers = a.courses.map((x) => x.container_id)
        return a
      })
      //create  course containers array for the access code that is being crating
      let data = values
      data.course_containers = data.course_containers.map((x) => x.container_id)
      data.is_active = values?.is_active === "active" ? 1 : 0
      newCodes = [...newClientsAccess, data]
    }
    return newCodes
  }

  const setLogo = (uploadRes, loadingAction, setFieldValue) => {
    setUploadImg(uploadRes.data)
    const image = JSON.stringify({
      url: uploadRes.data.url,
      secure_url: uploadRes.data.secure_url,
      originial_filename: uploadRes.data.original_filename,
      version: uploadRes.data.version,
      format: uploadRes.data.format,
      public_id: uploadRes.data.public_id,
    })
    setFieldValue("image", image)
    loadingAction(false)
  }
  const changeCode = (values) => {
    setLoading(true)

    if (action === "edit") {
      createAccessCode(newCodesFunc(values))
        .then((res) => {
          openSnackBar({ message: "Client Access Code updated" })
          setLoading(false)
          handleClose()
        })
        .catch((err) => {
          openSnackBar({ message: "Error. Failed to update Client" })
          setLoading(false)
        })
    } else {
      checkAccessCode(values?.code)
        .then(() => {
          createAccessCode(newCodesFunc(values)).then((res) => {
            openSnackBar({ message: "Client Access Code updated" })
            handleClose()
            setLoading(false)
          })
        })
        .catch((err) => {
          openSnackBar({ message: "Error. Failed to update Client" })
          setLoading(false)
        })
    }
  }
  const appLink = `${import.meta.env.VITE_APP_URL}/access-code?access_code=${
    formRef?.current?.values?.code
  }`

  const handleRemove = () => {
    if (accessLink?.is_active === 1) {
      openSnackBar({ message: "Access Link active" })
    } else {
      let data = {
        title: "Confirm Delete Acces link",
        subtitle: "",
        type: "delete",
        confirmWord: "delete",
      }
      openDialog("confirmationTypingDialog", data, false).then(() => {
        let newCodes = client.access_codes.filter((a) => {
          if (Number(a?.id) !== Number(accessLink?.id)) {
            let data = a
            data.course_containers = a.courses.map((x) => x.container_id)
            return data
          }
        })
        setAccessCodeMutation
          .mutateAsync(newCodes)
          .then((res) => {
            serialize("client", res.data).then((serializedData) => {
              return queryClient.setQueryData("clients", (oldData) => {
                let newArray = []
                newArray = oldData.map((x) => {
                  if (x.id !== serializedData.id) {
                    return x
                  } else {
                    return serializedData
                  }
                })
                return newArray
              })
            })
          })
          .catch((err) => {
            openSnackBar({ message: "Error. Failed to update Client" })
            setLoading(false)
          })

        handleClose()
      })
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      classes={{ paperWidthXs: classes.dialogWrapper }}
      fullScreen={smDown}
    >
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between">
          <HoverText
            data={client?.name}
            size={30}
            variant="h3"
            className={classes.title}
          />
          <Typography variant="subtitle1">{"ID: " + client?.id}</Typography>
        </Box>
      </DialogTitle>
      <Formik
        initialValues={{
          id: accessLink?.id || undefined,
          client_id: client?.id,
          code: accessLink?.code || "",
          is_active: accessLink?.is_active ? "active" : "inactive",
          seats: accessLink?.seats || "",
          expiration: accessLink.expiration
            ? dayjs(accessLink?.expiration)
            : null,

          course_containers: accessLink?.courses || [],
          description: accessLink?.description || "",
          image: accessLink?.image || "",
          amazon_sso: accessLink?.amazon_sso === 0 ? false : true,
          facebook_sso: accessLink?.facebook_sso === 0 ? false : true,
          github_sso: accessLink?.github_sso === 0 ? false : true,
          google_sso: accessLink?.google_sso === 0 ? false : true,
          video: accessLink?.video || null,
        }}
        onSubmit={changeCode}
        validationSchema={schema}
        innerRef={formRef}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          handleBlur,
          handleSubmit,
          setFieldValue,
          dirty,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogContent classes={{ root: classes.contentContainer }}>
                <Box>
                  <Box position="relative">
                    <Box className={classes.topRow}>
                      <Box width="50%" className={classes.codeNameBox}>
                        <Typography color="secondary" className={classes.title}>
                          Access Code
                        </Typography>
                        <TextField
                          type="text"
                          name="code"
                          id="code"
                          value={values.code}
                          variant="filled"
                          size="small"
                          onChange={handleChange}
                          className={classes.textField}
                          onBlur={handleBlur}
                          error={touched.code && errors.code}
                          helperText={
                            <span>
                              {(touched.code && errors.code) || accessCodeUsed
                                ? "Acces Code in Use"
                                : ""}
                            </span>
                          }
                          FormHelperTextProps={{
                            className: classes.formHelperText,
                          }}
                        />
                      </Box>
                      <Box width="40%" classes={{ root: classes.radioBox }}>
                        <FormControl component="fieldset">
                          <RadioGroup
                            name="is_active"
                            value={values.is_active}
                            onChange={handleChange}
                          >
                            <Box
                              display="flex"
                              marginTop="30px"
                              className={classes.radioSubBox}
                            >
                              <FormControlLabel
                                value="active"
                                control={
                                  <Radio
                                    size="small"
                                    classes={{ checked: classes.active }}
                                    color="default"
                                  />
                                }
                                label="Active"
                              />
                              <FormControlLabel
                                value="inactive"
                                control={
                                  <Radio
                                    size="small"
                                    classes={{ checked: classes.inactive }}
                                    color="default"
                                  />
                                }
                                label="Inactive"
                              />
                            </Box>
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    </Box>

                    <Box className={classes.rowTwo}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        width="30%"
                        className={classes.rowTwoBox}
                      >
                        <Typography color="secondary" className={classes.title}>
                          Total seats available
                        </Typography>
                        <TextField
                          name="seats"
                          id="seats"
                          value={values.seats}
                          variant="filled"
                          size="small"
                          InputProps={{
                            inputComponent: MaskedSeats,
                          }}
                          onChange={handleChange}
                          className={classes.textField}
                          onBlur={handleBlur}
                          helperText={
                            <span>{touched.seats && errors.seats}</span>
                          }
                          error={touched.seats && errors.seats}
                          FormHelperTextProps={{
                            className: classes.formHelperText,
                          }}
                        />
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        width="30%"
                        className={classes.rowTwoBox}
                      >
                        <Typography color="secondary" className={classes.title}>
                          Active seats
                        </Typography>
                        <TextField
                          disabled
                          defaultValue={accessLink?.uses}
                          InputProps={{
                            inputComponent: MaskedSeats,
                            classes: { root: classes.whiteBackgroundColor },
                          }}
                          variant="filled"
                          size="small"
                        />
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        width="30%"
                        className={classes.rowTwoBox}
                      >
                        <Typography color="secondary" className={classes.title}>
                          Expiration Date
                        </Typography>
                        <input
                          id="expiration-date-input"
                          className="bg-[#F4F4F4] p-2 rounded-md w-full"
                          type="date"
                          name="dateInput"
                          value={dayjs(values.expiration).format("YYYY-MM-DD")}
                          min={dayjs().format("YYYY-MM-DD")}
                          onChange={(e) => {
                            setFieldValue("expiration", e.target.value)
                          }}
                        />
                        {errors.expiration ? (
                          <p className={classes.expirationError}>
                            Expiration Date is required
                          </p>
                        ) : (
                          ""
                        )}
                      </Box>
                    </Box>

                    <Typography color="secondary" className={classes.title}>
                      Select Course
                    </Typography>
                    <Autocomplete
                      id="courses"
                      name="course_containers"
                      size="small"
                      options={publishedCourses || []}
                      getOptionLabel={(option) => option.name || ""}
                      getOptionDisabled={(option) =>
                        !!values?.course_containers?.find(
                          (x) => x.id === option.id,
                        )
                      }
                      renderInput={(params) => {
                        if (clearTextField) {
                          params.inputProps.value = ""
                        }

                        return (
                          <TextField
                            {...params}
                            variant="filled"
                            helperText={
                              <span>
                                {touched.course_containers &&
                                  errors.course_containers}
                              </span>
                            }
                            error={
                              touched.course_containers &&
                              errors.course_containers
                            }
                            FormHelperTextProps={{
                              className: classes.formHelperText,
                            }}
                            onChange={(val) => {
                              setClearTextField(false)
                            }}
                          />
                        )
                      }}
                      onChange={(e, val) => {
                        setFieldValue("course_containers", [
                          ...values.course_containers,
                          val,
                        ])
                        setClearTextField(true)
                      }}
                      openOnFocus
                      disableClearable
                      classes={{ inputRoot: classes.grayBackgroundColor }}
                    />

                    {values?.course_containers?.map((course, i) => {
                      return (
                        <Box
                          marginTop="8px"
                          minHeight={32}
                          maxHeight={64}
                          border="1px solid #17A5B1"
                          borderRadius="5px"
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          key={i}
                        >
                          <Typography
                            variant="subtitle1"
                            style={{
                              fontWeight: 600,
                              marginLeft: "8px",
                              overflowWrap: "break-word",
                            }}
                            color="secondary"
                          >
                            {course.name}
                          </Typography>
                          <IconButton
                            classes={{ root: classes.iconButton }}
                            styles={{ padding: 0 }}
                            onClick={() => {
                              let newCoursesArr =
                                values.course_containers.filter(
                                  (x) => x.id !== course?.id,
                                )
                              setFieldValue("course_containers", newCoursesArr)
                            }}
                            className={classes.removeCourseIcon}
                          >
                            <RemoveCircleIcon
                              color="inherit"
                              className={classes.removeCourseButton}
                            />
                          </IconButton>
                        </Box>
                      )
                    })}
                    <Box marginTop="16px">
                      <CounterHeader
                        title="Welcome Description"
                        value={values.description}
                        max={400}
                      />
                      <TextField
                        type="text"
                        name="description"
                        id="description"
                        value={values.description}
                        variant="filled"
                        size="small"
                        multiline
                        maxRows={4}
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.description && errors.description}
                        helperText={
                          <span>
                            {touched.description && errors.description}
                          </span>
                        }
                        FormHelperTextProps={{
                          className: classes.formHelperText,
                        }}
                        InputProps={{
                          classes: {
                            root: classes.descriptionInput,
                          },
                        }}
                      />
                      <Box
                        className={classes.ssoContainer}
                        width="100%"
                        display="flex"
                        justifyContent="space-between"
                        flexWrap="wrap"
                      >
                        <Box width="48%">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(values.amazon_sso)}
                                onChange={() =>
                                  setFieldValue(
                                    "amazon_sso",
                                    !values?.amazon_sso,
                                  )
                                }
                                name="amazon_sso"
                              />
                            }
                            label="Amazon SSO"
                          />
                        </Box>
                        <Box width="48%">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(values.facebook_sso)}
                                onChange={() =>
                                  setFieldValue(
                                    "facebook_sso",
                                    !values?.facebook_sso,
                                  )
                                }
                                name="facebook_sso"
                              />
                            }
                            label="Facebook SSO"
                          />
                        </Box>
                        <Box width="48%">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(values.github_sso)}
                                onChange={() =>
                                  setFieldValue(
                                    "github_sso",
                                    !values?.github_sso,
                                  )
                                }
                                name="github_sso"
                              />
                            }
                            label="Github SSO"
                          />
                        </Box>
                        <Box width="48%">
                          <FormControlLabel
                            control={
                              <Switch
                                disabled
                                checked={Boolean(values.google_sso)}
                                onChange={() =>
                                  setFieldValue(
                                    "github_sso",
                                    !values?.google_sso,
                                  )
                                }
                                name="google_sso"
                              />
                            }
                            label="Google SSO"
                          />
                        </Box>
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box width="48%">
                            <Typography
                              color="secondary"
                              className={classes.title}
                            >
                              Logo
                            </Typography>
                            <LogoUpload
                              cloudinaryUploadPreset={
                                import.meta.env
                                  .VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
                              }
                              value={values.image}
                              uploadImg={uploadImg}
                              onUpload={setLogo}
                              setFieldValue={setFieldValue}
                            />
                            {errors.image ? (
                              <Typography className={classes.imgError}>
                                {errors.image}
                              </Typography>
                            ) : null}
                          </Box>
                          <Box width="48%">
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              position="relative"
                              top="50%"
                            >
                              <TextField
                                disabled
                                id="link"
                                name="link"
                                variant="filled"
                                value={values?.code}
                                style={{
                                  width: "85%",
                                  height: "100%",
                                  marginRight: "8px",
                                }}
                              />
                              <CopyToClipboard
                                text={appLink}
                                onCopy={() =>
                                  openSnackBar({ message: "Link copied" })
                                }
                                options={{ format: "text/plain" }}
                              >
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  disableElevation
                                  size="small"
                                  disabled={formRef?.current?.dirty}
                                  classes={{
                                    root: classes.copyButton,
                                    label: classes.copyLabel,
                                  }}
                                  style={
                                    values?.code?.length === 0
                                      ? { display: "none" }
                                      : null
                                  }
                                >
                                  Copy
                                </Button>
                              </CopyToClipboard>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box marginTop="20px">
                    {uploadVideo ? (
                      <Box>
                        <ReactPlayer
                          className="react-player fixed-bottom"
                          url={uploadVideo}
                          width="100%"
                          height="100%"
                          controls={true}
                        />
                        <Box
                          display="flex"
                          justifyContent="center"
                          marginTop="10px"
                        >
                          <Button
                            onClick={() => {
                              setUploadVideo(null)
                              setFieldValue("video", null)
                            }}
                          >
                            Remove Video
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <AccessCodeVideoUpload
                        setUploadVideo={setUploadVideo}
                        uploadVideo={uploadVideo}
                        cloudinaryUploadPreset={
                          import.meta.env
                            .REACT_APP_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
                        }
                        setFieldValue={setFieldValue}
                        value={values.video}
                        setVideoLoading={setVideoLoading}
                      />
                    )}
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions classes={{ root: classes.dialogActions }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  margin="16px"
                  className={classes.buttonsContainer}
                >
                  {action === "edit" ? (
                    <Box className={classes.topRowButton}>
                      <Button
                        className={classes.remove}
                        onClick={() => handleRemove()}
                      >
                        Remove Access Link
                      </Button>
                    </Box>
                  ) : (
                    <Box />
                  )}
                  <Box className={classes.bottomRowButtons}>
                    <Button
                      className={classes.cancel}
                      onClick={() => handleClose()}
                    >
                      Close
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      type="submit"
                      disabled={videoLoading}
                    >
                      {loading ? (
                        <CircularProgress
                          size={20}
                          style={{ color: "white" }}
                        />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default CreateEditClientDialog
